if (!window.CusaPetitionSponsorshipButtons) {
	/**
	 * Manages sponsorship button interactions on the petition contribution section.
	 * Handles add-to-cart, loading/error states, cart drawer rendering, and the
	 * post-purchase collapse-offer / show-success-message transition.
	 * Initialised once on DOMContentLoaded against #petition-contribute.
	 */
	class CusaPetitionSponsorshipButtons {
		/**
		 * @param {HTMLElement} root - The #petition-contribute section element
		 */
		constructor(root) {
			this.root = root;
			this.offerContainer = root.querySelector('#petition-contribution-offer');
			this.buttonContainer = root.querySelector('#petition-sponsorship-btns');
			this.successMessage = root.querySelector('#petition-contribute-success-message');
			this.buttons = Array.from(root.querySelectorAll('[data-cusa-petition-sponsorship-button]'));
			this.isSubmitting = false;

			if (!this.buttonContainer || !this.buttons.length) {
				return;
			}

			this.buttons.forEach((button) => {
				button.addEventListener('click', this.handleClick);
			});
		}

		/**
		 * Click handler for sponsorship buttons — validates data attributes, sets submitting
		 * state, calls addToCart, then delegates to handleSuccess or error recovery.
		 * @param {MouseEvent} event
		 * @returns {Promise<void>}
		 */
		handleClick = async (event) => {
			if (this.isSubmitting) return;

			const clickedButton = event.currentTarget;
			const variantId = clickedButton.dataset.variantId;
			const petitionHandle = clickedButton.dataset.petitionHandle;

			if (!variantId || !petitionHandle) {
				this.showErrorToast();
				return;
			}

			// Fire on the click itself, not on add-to-cart success — this measures tier
			// selection, and a failed cart call is still a selection the user made.
			window.CusaPetitionFunnelAnalytics?.trackContribution(clickedButton.dataset.dollarAmount);

			this.isSubmitting = true;
			this.buttonContainer.setAttribute('aria-busy', 'true');
			this.disableAllButtons(clickedButton);

			try {
				const responseData = await this.addToCart({
					variantId,
					petitionHandle
				});

				await this.handleSuccess(responseData);
			} catch (error) {
				console.warn('[CUSA Petition Sponsorship] add to cart failed', error);
				this.restoreButtons();
				this.showErrorToast();
			} finally {
				this.buttonContainer.removeAttribute('aria-busy');
				this.isSubmitting = false;
			}
		};

		/**
		 * Adds the sponsorship variant to the Shopify cart via the Cart API.
		 * Attaches section-rendering data so the cart drawer can update without a page reload.
		 * @param {{ variantId: string, petitionHandle: string }} params
		 * @returns {Promise<Object>} Parsed cart response data
		 * @throws {Object} Cart API error response if the request fails
		 */
		addToCart = async ({ variantId, petitionHandle }) => {
			const cartDrawer = document.querySelector('cart-drawer');
			const cartNotification = document.querySelector('cart-notification');
			const cartComponent = cartDrawer || cartNotification;
			const formData = new FormData();
			formData.append('id', variantId);
			formData.append('quantity', '1');
			formData.append('properties[_sponsored-petition-handle]', petitionHandle);

			if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
				formData.append(
					'sections',
					cartComponent.getSectionsToRender().map((section) => section.id)
				);
				formData.append('sections_url', window.location.pathname);
				if (typeof cartComponent.setActiveElement === 'function') {
					cartComponent.setActiveElement(document.activeElement);
				}
			}

			const config =
				typeof window.fetchConfig === 'function'
					? window.fetchConfig('javascript')
					: {
							method: 'POST',
							headers: {
								'X-Requested-With': 'XMLHttpRequest'
							}
						};

			if (!config.headers) config.headers = {};
			config.headers['X-Requested-With'] = 'XMLHttpRequest';
			delete config.headers['Content-Type'];
			config.body = formData;

			const addBaseUrl = window?.routes?.cart_add_url || '/cart/add';
			const addUrl = addBaseUrl.endsWith('.js') ? addBaseUrl : `${addBaseUrl}.js`;
			const response = await fetch(addUrl, config);
			const data = await response.json();

			if (!response.ok || data?.status) {
				throw data;
			}

			return data;
		};

		/**
		 * Disables all sponsorship buttons and shows a loading spinner on the clicked one.
		 * @param {HTMLButtonElement} clickedButton - The button that was clicked
		 * @returns {void}
		 */
		disableAllButtons = (clickedButton) => {
			this.buttons.forEach((button) => {
				button.disabled = true;
				button.setAttribute('aria-disabled', 'true');

				if (button === clickedButton) {
					button.classList.add('loading');
					button.querySelector('[data-cusa-petition-sponsorship-text]')?.classList.add('hidden');
					button.querySelector('.loading__spinner')?.classList.remove('hidden');
				}
			});
		};

		/**
		 * Re-enables all sponsorship buttons and restores their original content.
		 * @returns {void}
		 */
		restoreButtons = () => {
			this.buttons.forEach((button) => {
				button.disabled = false;
				button.removeAttribute('aria-disabled');
				button.classList.remove('loading');
				button.querySelector('[data-cusa-petition-sponsorship-text]')?.classList.remove('hidden');
				button.querySelector('.loading__spinner')?.classList.add('hidden');
			});
		};

		/**
		 * Runs after a successful add-to-cart: collapses the offer UI, shows the success
		 * message, renders the cart drawer contents, and opens the drawer.
		 * @param {Object} cartData - Parsed response from the Shopify Cart API
		 * @returns {Promise<void>}
		 */
		handleSuccess = async (cartData) => {
			const cartDrawer = document.querySelector('cart-drawer');
			const cartNotification = document.querySelector('cart-notification');
			const cartComponent = cartDrawer || cartNotification;
			const usingCartDrawer = cartComponent === cartDrawer;

			await this.ensureReadyForDrawerOpen();
			await this.collapseContributionOffer();
			this.showSuccessMessage();
			await this.syncRevealContainerHeight();

			if (cartComponent && cartComponent.classList?.contains('is-empty')) {
				cartComponent.classList.remove('is-empty');
			}

			const drawerInner = cartDrawer?.querySelector('.drawer__inner');
			if (drawerInner?.classList.contains('is-empty')) {
				drawerInner.classList.remove('is-empty');
			}

			if (usingCartDrawer && cartDrawer) {
				this.renderDrawerContentsWithoutOpening(cartDrawer, cartData);
			} else if (cartComponent && typeof cartComponent.renderContents === 'function') {
				cartComponent.renderContents(cartData);
			}

			if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
				window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
					source: 'cusa-petition-sponsorship-buttons',
					cartData
				});
			}

			await new Promise((resolve) => setTimeout(resolve, 1000));

			if (usingCartDrawer && cartDrawer && typeof cartDrawer.open === 'function') {
				const cartTrigger = document.querySelector('#cart-icon-bubble') || undefined;
				cartDrawer.open(cartTrigger);
				await this.syncRevealContainerHeight();
			}
		};

		/**
		 * Updates the cart drawer's section HTML without triggering its open animation.
		 * @param {HTMLElement} cartDrawer - The cart-drawer custom element
		 * @param {Object} parsedState - Parsed cart response containing section HTML
		 * @returns {void}
		 */
		renderDrawerContentsWithoutOpening = (cartDrawer, parsedState) => {
			if (!cartDrawer || !parsedState) return;

			const drawerInner = cartDrawer.querySelector('.drawer__inner');
			if (drawerInner?.classList.contains('is-empty')) {
				drawerInner.classList.remove('is-empty');
			}

			cartDrawer.productId = parsedState.id;

			if (
				typeof cartDrawer.getSectionsToRender === 'function' &&
				typeof cartDrawer.getSectionInnerHTML === 'function'
			) {
				cartDrawer.getSectionsToRender().forEach((section) => {
					const sectionElement = section.selector
						? document.querySelector(section.selector)
						: document.getElementById(section.id);

					if (!sectionElement || !parsedState.sections?.[section.id]) return;

					sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
				});
			}
		};

		/**
		 * Animates the contribution offer container to zero height, then hides it.
		 * @returns {Promise<void>} Resolves after the collapse animation completes (~450ms)
		 */
		collapseContributionOffer = () => {
			return new Promise((resolve) => {
				const container = this.offerContainer || this.buttonContainer;
				if (!container) {
					resolve();
					return;
				}

				const startHeight = container.scrollHeight;
				container.style.height = `${startHeight}px`;
				container.style.overflow = 'hidden';
				container.style.transition = 'height var(--duration-extra-long) ease-out';

				requestAnimationFrame(() => {
					container.style.height = '0px';
				});

				window.setTimeout(() => {
					container.classList.add('hidden');
					container.style.removeProperty('height');
					container.style.removeProperty('overflow');
					container.style.removeProperty('transition');

					resolve();
				}, 450);
			});
		};

		/**
		 * Reveals the post-purchase success message.
		 * @returns {void}
		 */
		showSuccessMessage = () => {
			if (!this.successMessage) return;
			this.successMessage.classList.remove('hidden');
		};

		/**
		 * Updates the nearest .reveal ancestor's height to fit its current content.
		 * Required after any DOM change that alters the reveal container's natural height.
		 * @returns {Promise<void>}
		 */
		syncRevealContainerHeight = () => {
			return new Promise((resolve) => {
				const revealContainer = this.root.closest('.reveal');
				if (!revealContainer) {
					resolve();
					return;
				}

				requestAnimationFrame(() => {
					revealContainer.style.removeProperty('height');
					revealContainer.style.height = 'auto';
					resolve();
				});
			});
		};

		/**
		 * Scrolls to the top of the page and waits for the scroll to settle before opening
		 * the cart drawer, preventing layout jumps from overlapping transitions.
		 * @returns {Promise<void>}
		 */
		ensureReadyForDrawerOpen = async () => {
			window.scrollTo({
				top: 0,
				behavior: 'smooth'
			});

			await this.waitForScrollToStop();
		};

		/**
		 * Resolves once the page scroll position has been stable for a set duration,
		 * or after a maximum wait time — whichever comes first.
		 * @param {number} [maxWait=2000] - Maximum time to wait in milliseconds
		 * @param {number} [stableMs=180] - Duration of scroll inactivity considered "stopped"
		 * @returns {Promise<void>}
		 */
		waitForScrollToStop = (maxWait = 2000, stableMs = 180) => {
			return new Promise((resolve) => {
				const start = performance.now();
				let lastY = window.scrollY;
				let lastChange = start;

				const tick = () => {
					const now = performance.now();
					const currentY = window.scrollY;

					if (Math.abs(currentY - lastY) > 1) {
						lastY = currentY;
						lastChange = now;
					}

					if (now - lastChange >= stableMs || now - start >= maxWait) {
						resolve();
						return;
					}

					window.requestAnimationFrame(tick);
				};

				window.requestAnimationFrame(tick);
			});
		};

		/**
		 * Shows an error toast notification, or logs a fallback warning if the
		 * toast system is unavailable.
		 * @returns {void}
		 */
		showErrorToast = () => {
			if (window.CusaToastNotification?.create) {
				window.CusaToastNotification.create({
					type: 'error',
					title: 'Unable to add sponsorship',
					message: 'We couldn’t add your sponsorship. Please try again.',
					duration: 'infinite'
				});
				return;
			}

			console.warn('We couldn’t add your sponsorship. Please try again.');
		};
	}

	window.CusaPetitionSponsorshipButtons = CusaPetitionSponsorshipButtons;

	window.addEventListener('DOMContentLoaded', () => {
		const root = document.getElementById('petition-contribute');
		if (root) {
			new CusaPetitionSponsorshipButtons(root);
		}
	});
}
